/**
 * commandCenterTools.js — Single source of truth for the Command Center.
 *
 * Every tool listed here MUST correspond to an actual route in App.js.
 * Priority is a base score (higher = surfaces sooner); the smart
 * prioritizer in utils/prioritizeTools.js can re-order this list based
 * on user context (open cases, drafts, recent issue category, etc).
 */

export const CATEGORIES = [
  {
    id: 'get-answers',
    title: 'Get Answers',
    subtitle: 'Understand your rights, contract, and policies before you act.',
    accent: '#22d3ee',
  },
  {
    id: 'protect-rights',
    title: 'Protect Your Rights',
    subtitle: 'Prepare for discipline, investigations, and steward representation.',
    accent: '#facc15',
  },
  {
    id: 'build-case',
    title: 'Build Your Case',
    subtitle: 'Organize facts, evidence, and timelines for a strong record.',
    accent: '#a78bfa',
  },
  {
    id: 'follow-up',
    title: 'Follow Up',
    subtitle: 'Track saved work, notifications, and next steps.',
    accent: '#4ade80',
  },
];

export const commandCenterTools = [
  // ── GET ANSWERS ─────────────────────────────────────────────────────
  {
    id: 'ask-stewie',
    title: 'AskStewie Q&A',
    description: 'Get plain-English answers backed by your contract, MOAs, and policies.',
    category: 'get-answers',
    icon: 'MessageCircle',
    route: '/ask-stewie',
    actionLabel: 'Ask a Question',
    keywords: ['question', 'ask', 'contract', 'answer', 'help', 'chat', 'q&a'],
    priority: 100,
    available: true,
  },
  {
    id: 'library',
    title: 'Contract Search',
    description: 'Browse and search every CBA, MOA, and side letter in the library.',
    category: 'get-answers',
    icon: 'BookOpen',
    route: '/library',
    actionLabel: 'Search Contracts',
    keywords: ['library', 'contract', 'cba', 'moa', 'documents', 'search', 'policy'],
    priority: 80,
    available: true,
  },
  {
    id: 'safety-rules',
    title: 'Safety Guidance',
    description: 'Look up safety rules, LOTO, PPE, and emergency procedures.',
    category: 'get-answers',
    icon: 'ShieldCheck',
    route: '/safety-rules',
    actionLabel: 'Open Safety Rules',
    keywords: ['safety', 'ppe', 'loto', 'osha', 'emergency', 'hazard', 'lockout', 'tagout'],
    priority: 70,
    available: true,
  },
  {
    id: 'worker-voice',
    title: 'Worker Voice',
    description: 'See trending workplace concerns and what other stewards are seeing.',
    category: 'get-answers',
    icon: 'Users',
    route: '/worker-voice',
    actionLabel: 'View Trends',
    keywords: ['worker', 'voice', 'trends', 'community', 'insights'],
    priority: 40,
    available: true,
  },

  // ── PROTECT YOUR RIGHTS ─────────────────────────────────────────────
  {
    id: 'discipline-predictor',
    title: 'Discipline Predictor',
    description: 'Understand the likely level of discipline and factors that affect the outcome.',
    category: 'protect-rights',
    icon: 'AlertTriangle',
    route: '/conduct-predictor',
    actionLabel: 'Analyze Situation',
    keywords: ['discipline', 'discharge', 'termination', 'suspension', 'warning', 'writeup', 'write-up', 'corrective', 'conduct'],
    priority: 90,
    available: true,
  },
  {
    id: 'grievance-builder',
    title: 'Grievance Builder',
    description: 'Build a clear, organized grievance using your contract and case details.',
    category: 'protect-rights',
    icon: 'FileText',
    route: '/grievance-builder',
    actionLabel: 'Build Grievance',
    keywords: ['grievance', 'contract', 'violation', 'union', 'file', 'complaint'],
    priority: 95,
    available: true,
  },
  {
    id: 'fact-finder',
    title: 'Fact Finder Preparation',
    description: 'Prepare for a fact-finding meeting or investigation with structured prompts.',
    category: 'protect-rights',
    icon: 'Search',
    route: '/fact-finder',
    actionLabel: 'Prepare for Meeting',
    keywords: ['fact', 'finder', 'investigation', 'meeting', 'hr', 'interview', 'weingarten'],
    priority: 85,
    available: true,
  },
  {
    id: 'report-issue',
    title: 'Analyze a Workplace Issue',
    description: 'Describe what happened and Stewie will route you to the right tool.',
    category: 'protect-rights',
    icon: 'Zap',
    route: '/report-issue',
    actionLabel: 'Analyze Issue',
    keywords: ['problem', 'issue', 'help', 'incident', 'report', 'analyze', 'workplace'],
    priority: 92,
    available: true,
  },

  // ── BUILD YOUR CASE ─────────────────────────────────────────────────
  {
    id: 'case-locker',
    title: 'Case Locker',
    description: 'Track incidents, evidence, witnesses, and timelines in one place.',
    category: 'build-case',
    icon: 'FolderLock',
    route: '/case-locker',
    actionLabel: 'Open Case Locker',
    keywords: ['case', 'locker', 'evidence', 'timeline', 'witnesses', 'notes', 'attachments', 'documents'],
    priority: 88,
    available: true,
  },
  {
    id: 'job-briefing',
    title: 'Job Briefing Builder',
    description: 'Build structured job briefings with hazards, PPE, and task planning.',
    category: 'build-case',
    icon: 'ClipboardList',
    route: '/job-briefing',
    actionLabel: 'Start Briefing',
    keywords: ['job', 'briefing', 'hazards', 'ppe', 'task', 'planning', 'pre-job'],
    priority: 55,
    available: true,
  },
  {
    id: 'statement-builder',
    title: 'Statement Builder',
    description: 'Turn scattered notes into a clear, factual written statement.',
    category: 'build-case',
    icon: 'Edit3',
    route: '/statement-builder',
    actionLabel: 'Draft Statement',
    keywords: ['statement', 'write', 'draft', 'narrative', 'letter', 'declaration'],
    priority: 50,
    available: true,
  },

  // ── FOLLOW UP ───────────────────────────────────────────────────────
  {
    id: 'your-work',
    title: 'Continue Your Drafts',
    description: 'Resume saved Fact Finder cases, grievance drafts, and job briefings.',
    category: 'follow-up',
    icon: 'Folder',
    route: '/dashboard/drafts',
    actionLabel: 'Open Drafts',
    keywords: ['drafts', 'saved', 'work', 'continue', 'resume', 'my work'],
    priority: 60,
    available: true,
  },
  {
    id: 'notifications',
    title: 'Notifications',
    description: 'Review deadlines, updates, and alerts from Stewie.',
    category: 'follow-up',
    icon: 'Bell',
    route: '/notifications',
    actionLabel: 'View Notifications',
    keywords: ['notifications', 'alerts', 'deadlines', 'updates', 'bell'],
    priority: 45,
    available: true,
  },
  {
    id: 'user-guide',
    title: 'User Guide',
    description: 'How to use every AskStewie tool, one guide per feature.',
    category: 'follow-up',
    icon: 'HelpCircle',
    route: '/user-guide',
    actionLabel: 'Read the Guide',
    keywords: ['help', 'guide', 'documentation', 'how to', 'tutorial', 'manual'],
    priority: 30,
    available: true,
  },
];

/** Map iconName → module import handled inside ToolCard.jsx */
export const TOOL_ICON_NAMES = [
  ...new Set(commandCenterTools.map((t) => t.icon)),
];
